/**
 * Cloud-Sync für V2-Projekte
 * 
 * Format: ZIP mit Dateistruktur
 * ├── meta.json (chatMessages, projectProgress, lastUsedModelId, etc.)
 * ├── code.html
 * ├── code.css
 * └── src/
 *     └── index.js
 */

import JSZip from 'jszip';
import { EDITOR_VIEW_NAME } from '../../core/constants';
import { getViewData, setViewData } from '../../core/views';
import { vfs } from './VirtualFileSystem';
import { FileDefinition } from './types';

/**
 * Meta-Daten die im ZIP gespeichert werden (nicht die Dateien selbst)
 */
interface V2CloudMeta {
  isV2: true;
  projectProgress?: any;
  projectFinished?: boolean;
  chatMessages?: any[];
  agentChatMessages?: any[];
  agentChatId?: string;
  lastUsedModelId?: string;
  userAssets?: any;
  projectName?: string;
  projectHint?: string;
  projectTask?: string;
  /** Zeitstempel Cloud-Save für Konfliktvergleich mit lokalem VFS */
  cloudSavedAt?: number;
  // Datei-Definitionen (Struktur, nicht Inhalt)
  fileDefinitions?: FileDefinition[];
}

/**
 * Erstellt ein ZIP mit allen V2-Projekt-Dateien und Meta-Daten
 * @returns Base64-encodierter ZIP-String für Cloud-Upload
 */
export async function createV2CloudZip(editorState: any): Promise<string> {
  // VFS initialisieren falls noch nicht geschehen
  try {
    await vfs.init();
  } catch (e) {
    console.warn('[V2CloudSync] VFS init failed (may already be initialized):', e);
  }
  
  const zip = new JSZip();
  
  const projectId = editorState?.project?.id || editorState?.project?.project?.replace(/\s+/g, '_');
  // v2EditorChanges kann Objekt oder Map sein (z. B. nach onFileDelete) – immer als Objekt nutzen
  const raw = editorState?.data?.v2EditorChanges;
  const v2Changes: Record<string, string> =
    raw != null && typeof raw === 'object'
      ? (raw instanceof Map ? Object.fromEntries(raw) : (raw as Record<string, string>))
      : {};
  const projectFiles: FileDefinition[] = editorState?.data?.projectContent?.tabs || [];
  
  // 1. Meta-Daten sammeln (inkl. AgentChat + Timestamp)
  const meta: V2CloudMeta = {
    isV2: true,
    projectProgress: editorState?.data?.projectProgress,
    projectFinished: editorState?.data?.projectFinished,
    chatMessages: editorState?.data?.chatMessages || [],
    agentChatMessages: editorState?.data?.agentChatMessages || [],
    agentChatId: editorState?.data?.agentChatId,
    lastUsedModelId: editorState?.data?.lastUsedModelId,
    userAssets: editorState?.data?.userAssets,
    projectName: editorState?.project?.project,
    projectHint: editorState?.project?.hint,
    projectTask: editorState?.project?.task,
    cloudSavedAt: Date.now(),
    fileDefinitions: projectFiles,
  };
  
  zip.file('meta.json', JSON.stringify(meta, null, 2));
  
  // 2. Alle Projekt-Dateien hinzufügen
  const projectRoot = vfs.getProjectRoot(projectId);
  
  for (const fileDef of projectFiles) {
    let content = '';
    
    // Priorisiere Editor-Änderungen (Objekt-Zugriff), dann VFS
    if (fileDef.path in v2Changes && v2Changes[fileDef.path] !== undefined) {
      content = v2Changes[fileDef.path] || '';
    } else {
      const fullPath = `${projectRoot}/${fileDef.path}`;
      try {
        if (await vfs.exists(fullPath)) {
          content = await vfs.readFile(fullPath);
        }
      } catch (e) {
        console.warn(`[V2CloudSync] Could not read ${fileDef.path} from VFS:`, e);
      }
    }
    
    // Datei mit relativem Pfad ins ZIP (unterstützt Unterordner)
    zip.file(fileDef.path, content);
  }
  
  // 3. ZIP als Base64 generieren
  const base64 = await zip.generateAsync({ type: 'base64' });
  
  console.log(`[V2CloudSync] Created ZIP with ${projectFiles.length} files + meta.json`);
  return base64;
}

/**
 * Lädt V2-Projekt-Daten aus einem Cloud-ZIP
 * @param base64Data Base64-encodierter ZIP-String
 * @param projectId Projekt-ID für VFS-Pfad
 */
export async function loadV2CloudZip(base64Data: string, projectId: string): Promise<void> {
  // VFS initialisieren falls noch nicht geschehen
  try {
    await vfs.init();
  } catch (e) {
    console.warn('[V2CloudSync] VFS init failed (may already be initialized):', e);
  }
  
  const zip = await JSZip.loadAsync(base64Data, { base64: true });
  
  let meta: V2CloudMeta | null = null;
  const files: Map<string, string> = new Map();
  
  // 1. ZIP-Inhalt durchgehen
  for (const [relativePath, zipEntry] of Object.entries(zip.files)) {
    if (zipEntry.dir) continue; // Ordner überspringen
    
    const content = await zipEntry.async('text');
    
    if (relativePath === 'meta.json') {
      try {
        meta = JSON.parse(content);
      } catch (e) {
        console.error('[V2CloudSync] Failed to parse meta.json:', e);
      }
    } else {
      files.set(relativePath, content);
    }
  }
  
  if (!meta || !meta.isV2) {
    console.warn('[V2CloudSync] No valid V2 meta found in ZIP');
    return;
  }

  const projectRoot = vfs.getProjectRoot(projectId);
  const editorState = getViewData(EDITOR_VIEW_NAME);
  const lastVfsWriteAt = editorState?.data?.lastVfsWriteAt as number | undefined;
  const cloudSavedAt = meta.cloudSavedAt ?? 0;
  const useLocalVFS =
    typeof lastVfsWriteAt === 'number' && lastVfsWriteAt > cloudSavedAt;

  let contentMap: Record<string, string> = {};

  if (useLocalVFS) {
    // Lokales VFS ist neuer: nicht überschreiben, Inhalte aus VFS lesen
    const fileDefs = meta.fileDefinitions?.length
      ? meta.fileDefinitions
      : editorState?.data?.projectContent?.tabs ?? [];
    for (const def of fileDefs) {
      const path = def.path || (def as any).path;
      if (!path) continue;
      const fullPath = `${projectRoot}/${path}`;
      try {
        if (await vfs.exists(fullPath)) {
          contentMap[path] = await vfs.readFile(fullPath);
        }
      } catch (e) {
        console.warn(`[V2CloudSync] Could not read local VFS ${path}:`, e);
      }
    }
    console.log('[V2CloudSync] Using local VFS (newer than cloud), files:', Object.keys(contentMap).length);
  } else {
    // Cloud neuer oder kein lokaler Timestamp: Cloud-Dateien ins VFS schreiben
    for (const [relativePath, cloudContent] of files) {
      const fullPath = `${projectRoot}/${relativePath}`;
      let content = cloudContent;

      // Guard: Leere Cloud-Datei ueberschreibt nicht vorhandene VFS-Daten mit Inhalt
      if (!content) {
        try {
          if (await vfs.exists(fullPath)) {
            const existing = await vfs.readFile(fullPath);
            if (existing) {
              contentMap[relativePath] = existing;
              console.warn(`[V2CloudSync] Skipping empty cloud file, keeping VFS: ${relativePath}`);
              continue;
            }
          }
        } catch (e) {
          /* VFS read failed, use cloud content */
        }
      }

      const dirPath = fullPath.substring(0, fullPath.lastIndexOf('/'));
      if (dirPath && dirPath !== projectRoot) {
        try {
          await vfs.ensureDir(dirPath);
        } catch (e) {
          /* Ordner existiert evtl. schon */
        }
      }
      try {
        await vfs.writeFile(fullPath, content);
      } catch (e) {
        console.error(`[V2CloudSync] Failed to write ${relativePath} to VFS:`, e);
      }
      contentMap[relativePath] = content;
    }
    console.log(`[V2CloudSync] Loaded ${files.size} files into VFS`);
  }

  // Meta + angezeigte Inhalte (v2EditorChanges, tabs mit .code) in editorState
  const fileDefs = meta.fileDefinitions?.length
    ? meta.fileDefinitions
    : editorState?.data?.projectContent?.tabs ?? [];
  const tabsWithCode = fileDefs.map((def: any) => ({
    ...def,
    // || statt ??: leere Cloud-Strings fallen auf Server-Baseline (def.code) zurueck
    code: contentMap[def.path] || def.code || '',
  }));

  editorState.data = {
    ...editorState.data,
    projectProgress: meta.projectProgress,
    projectFinished: meta.projectFinished,
    chatMessages: meta.chatMessages || [],
    agentChatMessages: meta.agentChatMessages || [],
    agentChatId: meta.agentChatId,
    lastUsedModelId: meta.lastUsedModelId,
    userAssets: meta.userAssets,
    v2EditorChanges: contentMap,
  };

  if (meta.projectName) {
    editorState.project = {
      ...editorState.project,
      project: meta.projectName,
      hint: meta.projectHint,
      task: meta.projectTask,
    };
  }

  if (fileDefs.length > 0) {
    editorState.data.projectContent = {
      ...editorState.data.projectContent,
      tabs: tabsWithCode,
    };
  }

  setViewData(EDITOR_VIEW_NAME, editorState);
}

/**
 * Prüft ob ein Base64-String ein V2-ZIP ist
 */
export async function isV2CloudData(base64Data: string): Promise<boolean> {
  try {
    const zip = await JSZip.loadAsync(base64Data, { base64: true });
    
    // Prüfe ob meta.json existiert und isV2: true hat
    const metaFile = zip.file('meta.json');
    if (!metaFile) return false;
    
    const metaContent = await metaFile.async('text');
    const meta = JSON.parse(metaContent);
    
    return meta?.isV2 === true;
  } catch (e) {
    // Kein gültiges ZIP oder kein V2-Format
    return false;
  }
}

